/**
 * ChangePasswordModal Component
 *
 * Modal for forced password change on first login.
 * Shows when user.must_change_password is true.
 */

import { useState, FormEvent } from 'react'
import { X, Lock, AlertCircle, Loader2, CheckCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import axios from 'axios'
import env from '../config/env'

interface ChangePasswordModalProps {
  currentPassword: string
  accessToken: string
  onSuccess: () => void
  canClose?: boolean
}

const ChangePasswordModal = ({
  currentPassword,
  accessToken,
  onSuccess,
  canClose = false
}: ChangePasswordModalProps) => {
  const [newPassword, setNewPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [errors, setErrors] = useState<{ newPassword?: string }>({})

  // Real-time password validation checks
  const hasMinLength = newPassword.length >= 8
  const hasUppercase = /[A-Z]/.test(newPassword)
  const hasLowercase = /[a-z]/.test(newPassword)
  const hasNumber = /[0-9]/.test(newPassword)
  const isDifferentFromCurrent = newPassword && newPassword !== currentPassword

  const API_BASE_URL = env.API_URL || window.location.origin

  /**
   * Validate password strength (matches backend requirements)
   */
  const validatePassword = (password: string): string | null => {
    if (password.length < 8) {
      return 'Password must be at least 8 characters'
    }
    if (!/[A-Z]/.test(password)) {
      return 'Password must contain at least one uppercase letter'
    }
    if (!/[a-z]/.test(password)) {
      return 'Password must contain at least one lowercase letter'
    }
    if (!/[0-9]/.test(password)) {
      return 'Password must contain at least one number'
    }
    return null
  }

  /**
   * Validate form inputs
   */
  const validateForm = (): boolean => {
    const newErrors: { newPassword?: string } = {}

    // Validate new password
    const passwordError = validatePassword(newPassword)
    if (passwordError) {
      newErrors.newPassword = passwordError
    }

    // Check if new password is different from current
    if (newPassword === currentPassword) {
      newErrors.newPassword = 'New password must be different from your temporary password'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()

    // Clear previous errors
    setErrors({})

    // Validate form
    if (!validateForm()) {
      return
    }

    setIsLoading(true)

    try {
      await axios.post(
        `${API_BASE_URL}/api/users/change-password`,
        {
          current_password: currentPassword,
          new_password: newPassword
        },
        {
          headers: {
            Authorization: `Bearer ${accessToken}`
          },
          withCredentials: true
        }
      )

      toast.success('Password changed successfully!')
      onSuccess()
    } catch (err) {
      const error = err as { response?: { data?: { detail?: unknown } }; message?: unknown }

      let errorMessage = 'Failed to change password'

      if (error?.response?.data?.detail) {
        // FastAPI error detail
        if (Array.isArray(error.response.data.detail)) {
          // Validation errors
          errorMessage = error.response.data.detail.map((item: { msg?: unknown }) => String(item.msg)).join(', ')
        } else {
          errorMessage = String(error.response.data.detail)
        }
      } else if (error?.message) {
        errorMessage = String(error.message)
      }

      toast.error(errorMessage)

      if (errorMessage.includes('Current password is incorrect')) {
        setErrors({ newPassword: 'The current password verification failed. Please contact support.' })
      } else if (errorMessage.toLowerCase().includes('password')) {
        setErrors({ newPassword: errorMessage })
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="rq-modal-card !max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-900/30 rounded-lg border border-orange-700/40">
              <AlertCircle className="w-6 h-6 text-orange-300" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-[var(--rq-text)]">Change Password Required</h2>
              <p className="text-sm text-[var(--rq-text-soft)]">You must change your temporary password</p>
            </div>
          </div>
          {canClose && (
            <button
              onClick={onSuccess}
              className="text-[var(--rq-text-soft)] hover:text-[var(--rq-text)] transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* New Password */}
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-[var(--rq-text-soft)] mb-1">
              New Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-[var(--rq-text-soft)]" />
              <input
                id="newPassword"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className={`w-full pl-10 pr-4 py-2 rq-input-dark ${
                  errors.newPassword ? 'border-red-500' : ''
                }`}
                placeholder="Enter new password"
                disabled={isLoading}
              />
            </div>
            {errors.newPassword && (
              <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                <AlertCircle className="w-4 h-4" />
                {errors.newPassword}
              </p>
            )}

            {/* Real-time password validation feedback */}
            {newPassword && (
              <div className="mt-2 space-y-1">
                <div className={`text-xs flex items-center gap-1 ${hasMinLength ? 'text-emerald-500' : 'text-[var(--rq-text-soft)]'}`}>
                  {hasMinLength ? <CheckCircle className="w-3 h-3" /> : <div className="w-3 h-3 border border-[var(--rq-text-soft)] rounded-full" />}
                  <span>At least 8 characters</span>
                </div>
                <div className={`text-xs flex items-center gap-1 ${hasUppercase ? 'text-emerald-500' : 'text-[var(--rq-text-soft)]'}`}>
                  {hasUppercase ? <CheckCircle className="w-3 h-3" /> : <div className="w-3 h-3 border border-[var(--rq-text-soft)] rounded-full" />}
                  <span>One uppercase letter</span>
                </div>
                <div className={`text-xs flex items-center gap-1 ${hasLowercase ? 'text-emerald-500' : 'text-[var(--rq-text-soft)]'}`}>
                  {hasLowercase ? <CheckCircle className="w-3 h-3" /> : <div className="w-3 h-3 border border-[var(--rq-text-soft)] rounded-full" />}
                  <span>One lowercase letter</span>
                </div>
                <div className={`text-xs flex items-center gap-1 ${hasNumber ? 'text-emerald-500' : 'text-[var(--rq-text-soft)]'}`}>
                  {hasNumber ? <CheckCircle className="w-3 h-3" /> : <div className="w-3 h-3 border border-[var(--rq-text-soft)] rounded-full" />}
                  <span>One number</span>
                </div>
                <div className={`text-xs flex items-center gap-1 ${isDifferentFromCurrent ? 'text-emerald-500' : 'text-[var(--rq-text-soft)]'}`}>
                  {isDifferentFromCurrent ? <CheckCircle className="w-3 h-3" /> : <div className="w-3 h-3 border border-[var(--rq-text-soft)] rounded-full" />}
                  <span>Different from temporary password</span>
                </div>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={isLoading}
            className="w-full rq-btn-primary justify-center font-bold py-3 px-4 disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Changing Password...
              </>
            ) : (
              <>
                <CheckCircle className="w-5 h-5" />
                Change Password
              </>
            )}
          </button>
        </form>
      </div>
    </div>
  )
}

export default ChangePasswordModal
